import React, { useState, useEffect, useMemo } from 'react';
import { useBars } from '../hooks/useBars';
import { logAction, getCurrentAdmin } from '../services/logger';
import { supabase, supabaseAdmin } from '../lib/supabase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, X, CheckCheck, Search, ChevronRight, Minus } from 'lucide-react';
import toast from 'react-hot-toast';

interface MatchItem {
  id: string;
  home_team: string;
  away_team: string;
  match_date: string;
  match_time: string;
  competition: string;
  sport: string;
}

interface DiffusionItem {
  id: string;
  bar_id: string;
  match_id: string;
  status: string;
  confirmed_at: string | null;
  confirmed_by: string | null;
  bars: { id: string; name: string; probability?: number } | null;
}

export function DiffusionsPage() {
  const [matches, setMatches] = useState<MatchItem[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<MatchItem | null>(null);
  const [diffusions, setDiffusions] = useState<DiffusionItem[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [loadingDiffs, setLoadingDiffs] = useState(false);
  const [dateRange, setDateRange] = useState<'today' | 'tomorrow' | 'week' | 'all'>('all');
  const [sportFilter, setSportFilter] = useState('');
  const [competitionFilter, setCompetitionFilter] = useState('');
  const [matchDayFilter, setMatchDayFilter] = useState('');
  const [competitions, setCompetitions] = useState<string[]>([]);
  const [searchMatch, setSearchMatch] = useState('');
  const [showAddBar, setShowAddBar] = useState(false);
  const [addBarId, setAddBarId] = useState('');
  const [addBarSearch, setAddBarSearch] = useState('');
  const [addStatus, setAddStatus] = useState('probable');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { bars } = useBars();

  // Load competitions list
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    supabaseAdmin.from('matches').select('competition').gte('match_date', today).limit(500)
      .then(({ data }) => {
        const comps = Array.from(new Set((data || []).map(d => d.competition).filter(Boolean))).sort();
        setCompetitions(comps);
      });
  }, []);

  // Load matches
  useEffect(() => {
    loadMatches();
  }, [dateRange, sportFilter, competitionFilter]);

  // Realtime on diffusions
  useEffect(() => {
    const channel = supabase.channel('diffs-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'diffusions' }, () => {
        if (selectedMatch) loadDiffusionsForMatch(selectedMatch.id);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedMatch]);

  const loadMatches = async () => {
    setLoadingMatches(true);
    const today = new Date().toISOString().split('T')[0];
    let query = supabaseAdmin.from('matches')
      .select('id, home_team, away_team, match_date, match_time, competition, sport')
      .gte('match_date', today)
      .order('match_date').order('match_time')
      .limit(200);

    if (dateRange === 'today') query = query.eq('match_date', today);
    else if (dateRange === 'tomorrow') {
      const tm = new Date(); tm.setDate(tm.getDate() + 1);
      query = query.eq('match_date', tm.toISOString().split('T')[0]);
    } else if (dateRange === 'week') {
      const end = new Date(); end.setDate(end.getDate() + 7);
      query = query.lte('match_date', end.toISOString().split('T')[0]);
    }
    if (sportFilter) query = query.ilike('sport', sportFilter);
    if (competitionFilter) query = query.eq('competition', competitionFilter);

    const { data } = await query;
    setMatches(data || []);
    setLoadingMatches(false);
  };

  const loadDiffusionsForMatch = async (matchId: string) => {
    setLoadingDiffs(true);
    const { data } = await supabaseAdmin
      .from('diffusions')
      .select('id, bar_id, match_id, status, confirmed_at, confirmed_by, bars(id, name, probability)')
      .eq('match_id', matchId);
    setDiffusions((data || []) as unknown as DiffusionItem[]);
    setLoadingDiffs(false);
  };

  const selectMatch = (match: MatchItem) => {
    setSelectedMatch(match);
    setSelected(new Set());
    setShowAddBar(false);
    loadDiffusionsForMatch(match.id);
  };

  // Available match days (unique dates in current matches list)
  const matchDays = useMemo(() => {
    const days = Array.from(new Set(matches.map(m => m.match_date))).sort();
    return days;
  }, [matches]);

  const filteredMatches = useMemo(() => {
    let result = matches;
    if (matchDayFilter) {
      result = result.filter(m => m.match_date === matchDayFilter);
    }
    if (searchMatch) {
      const s = searchMatch.toLowerCase();
      result = result.filter(m =>
        m.home_team.toLowerCase().includes(s) ||
        m.away_team.toLowerCase().includes(s) ||
        m.competition.toLowerCase().includes(s)
      );
    }
    return result;
  }, [matches, matchDayFilter, searchMatch]);

  // Count diffusions per match for the list
  const [diffCounts, setDiffCounts] = useState<Record<string, { total: number; confirmed: number }>>({});
  useEffect(() => {
    if (matches.length === 0) return;
    const ids = matches.map(m => m.id);
    supabaseAdmin.from('diffusions').select('match_id, status').in('match_id', ids)
      .then(({ data }) => {
        const counts: Record<string, { total: number; confirmed: number }> = {};
        (data || []).forEach(d => {
          if (!counts[d.match_id]) counts[d.match_id] = { total: 0, confirmed: 0 };
          counts[d.match_id].total++;
          if (d.status === 'confirmed') counts[d.match_id].confirmed++;
        });
        setDiffCounts(counts);
      });
  }, [matches]);

  const handleStatusChange = async (diffId: string, newStatus: string, oldStatus: string, barName: string) => {
    if (newStatus === oldStatus) return;
    const admin = getCurrentAdmin();
    const matchName = selectedMatch ? `${selectedMatch.home_team} vs ${selectedMatch.away_team}` : '';
    await supabaseAdmin.from('diffusions').update({
      status: newStatus, confirmed_at: new Date().toISOString(), confirmed_by: 'admin',
    }).eq('id', diffId);
    const labels: Record<string, string> = { unconfirmed: 'non vérifié', probable: 'probable', confirmed: 'confirmé' };
    await logAction('confirm_diffusion', 'diffusion', diffId,
      { status: oldStatus }, { status: newStatus },
      `${admin?.name} a passé ${barName} en "${labels[newStatus]}" pour ${matchName}`);
    toast.success(newStatus === 'confirmed' ? `${barName} confirmé` : 'Statut mis à jour');
    if (selectedMatch) loadDiffusionsForMatch(selectedMatch.id);
  };

  const handleProbChange = async (barId: string, barName: string, oldProb: number, newProb: number) => {
    const admin = getCurrentAdmin();
    await supabaseAdmin.from('bars').update({ probability: newProb }).eq('id', barId);
    await logAction('update_bar', 'bar', barId,
      { probability: oldProb }, { probability: newProb },
      `${admin?.name} a changé la probabilité de ${barName} de ${oldProb}% à ${newProb}%`);
    // Refresh diffusions to get updated probability
    if (selectedMatch) loadDiffusionsForMatch(selectedMatch.id);
  };

  const handleAddBarToMatch = async () => {
    if (!addBarId || !selectedMatch) return;
    const admin = getCurrentAdmin();
    const bar = bars.find(b => b.id === addBarId);
    // Check if already exists
    const existing = diffusions.find(d => d.bar_id === addBarId);
    if (existing) { toast.error(`${bar?.name} est déjà associé à ce match`); return; }

    await supabaseAdmin.from('diffusions').insert({
      bar_id: addBarId, match_id: selectedMatch.id, status: addStatus,
      ...(addStatus !== 'unconfirmed' ? { confirmed_at: new Date().toISOString(), confirmed_by: 'admin' } : {}),
    });
    await logAction('confirm_diffusion', 'diffusion', null, null,
      { bar: bar?.name, status: addStatus },
      `${admin?.name} a ajouté ${bar?.name} pour ${selectedMatch.home_team} vs ${selectedMatch.away_team}`);
    toast.success(`${bar?.name} ajouté`);
    setAddBarId('');
    setShowAddBar(false);
    loadDiffusionsForMatch(selectedMatch.id);
  };

  const handleRemoveDiffusion = async (diff: DiffusionItem) => {
    const barName = diff.bars?.name || '?';
    if (!window.confirm(`Retirer ${barName} de ce match ?`)) return;
    const admin = getCurrentAdmin();
    await supabaseAdmin.from('diffusions').delete().eq('id', diff.id);
    await logAction('delete_diffusion', 'diffusion', diff.id,
      { bar: barName, match: selectedMatch ? `${selectedMatch.home_team} vs ${selectedMatch.away_team}` : '' },
      null,
      `${admin?.name} a retiré ${barName} de ${selectedMatch?.home_team} vs ${selectedMatch?.away_team}`);
    toast.success(`${barName} retiré`);
    if (selectedMatch) loadDiffusionsForMatch(selectedMatch.id);
  };

  const bulkUpdateStatus = async (newStatus: string) => {
    if (selected.size === 0) return;
    const admin = getCurrentAdmin();
    const ids = Array.from(selected);
    for (const id of ids) {
      await supabaseAdmin.from('diffusions').update({
        status: newStatus, confirmed_at: new Date().toISOString(), confirmed_by: 'admin',
      }).eq('id', id);
    }
    const labels: Record<string, string> = { unconfirmed: 'non vérifié', probable: 'probable', confirmed: 'confirmé' };
    await logAction('confirm_diffusion', 'diffusion', null, null,
      { count: ids.length, status: newStatus },
      `${admin?.name} a mis ${ids.length} bars en "${labels[newStatus]}" pour ${selectedMatch?.home_team} vs ${selectedMatch?.away_team}`);
    toast.success(`${ids.length} bars mis à jour`);
    setSelected(new Set());
    if (selectedMatch) loadDiffusionsForMatch(selectedMatch.id);
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  // Bars not yet assigned to selected match
  const availableBars = bars.filter(b => !diffusions.find(d => d.bar_id === b.id));

  return (
    <div className="diff-split" style={{ display: 'grid', gridTemplateColumns: selectedMatch ? '380px 1fr' : '1fr', gap: '16px' }}>
      {/* LEFT: Match list */}
      <div>
        {/* Quick filter buttons */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
          {['Coupe du Monde FIFA 2026', 'French Ligue 1', 'UEFA Champions League'].map(comp => {
            const isActive = competitionFilter === comp;
            const label = comp === 'Coupe du Monde FIFA 2026' ? 'CDM 2026' : comp === 'French Ligue 1' ? 'Ligue 1' : 'UCL';
            // Only show if competition exists
            if (!competitions.includes(comp)) return null;
            return (
              <button key={comp}
                onClick={() => { setCompetitionFilter(isActive ? '' : comp); setMatchDayFilter(''); setDateRange('all'); }}
                style={{
                  padding: '5px 12px', fontSize: '11px', fontWeight: 700, borderRadius: '14px', cursor: 'pointer',
                  border: isActive ? '1px solid var(--accent)' : '1px solid var(--border)',
                  background: isActive ? 'var(--accent)' : 'var(--bg-secondary)',
                  color: isActive ? '#FFFFFF' : 'var(--text-muted)',
                  transition: 'all 0.1s',
                }}
              >{label}</button>
            );
          })}
        </div>
        <div className="filters-row" style={{ marginBottom: '8px' }}>
          <select className="filter-select" value={competitionFilter} onChange={e => { setCompetitionFilter(e.target.value); setMatchDayFilter(''); }} style={{ flex: 1 }}>
            <option value="">Toutes compétitions</option>
            {competitions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filters-row" style={{ marginBottom: '8px' }}>
          <select className="filter-select" value={dateRange} onChange={e => { setDateRange(e.target.value as any); setMatchDayFilter(''); }} style={{ flex: 1 }}>
            <option value="today">Aujourd'hui</option>
            <option value="tomorrow">Demain</option>
            <option value="week">Cette semaine</option>
            <option value="all">Tout</option>
          </select>
          <select className="filter-select" value={sportFilter} onChange={e => setSportFilter(e.target.value)}>
            <option value="">Tous</option>
            <option value="football">Football</option>
            <option value="rugby">Rugby</option>
          </select>
        </div>

        {/* Match day chips */}
        {matchDays.length > 1 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <button
              onClick={() => setMatchDayFilter('')}
              style={{
                padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '14px', cursor: 'pointer',
                border: '1px solid var(--border)', transition: 'all 0.1s',
                background: !matchDayFilter ? 'var(--accent)' : 'var(--bg-secondary)',
                color: !matchDayFilter ? '#FFFFFF' : 'var(--text-muted)',
              }}
            >Tous</button>
            {matchDays.map(day => (
              <button
                key={day}
                onClick={() => setMatchDayFilter(day === matchDayFilter ? '' : day)}
                style={{
                  padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '14px', cursor: 'pointer',
                  border: '1px solid var(--border)', transition: 'all 0.1s',
                  background: matchDayFilter === day ? 'var(--accent)' : 'var(--bg-secondary)',
                  color: matchDayFilter === day ? '#FFFFFF' : 'var(--text-muted)',
                }}
              >
                {format(new Date(day + 'T00:00:00'), 'EEE d MMM', { locale: fr })}
                <span style={{ marginLeft: '4px', opacity: 0.7 }}>
                  ({matches.filter(m => m.match_date === day).length})
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="filter-search" style={{ marginBottom: '10px' }}>
          <Search size={14} />
          <input placeholder="Rechercher un match..." value={searchMatch} onChange={e => setSearchMatch(e.target.value)} />
        </div>

        <div className="card" style={{ padding: 0, maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
          {loadingMatches ? (
            <div className="skeleton-list">{[1,2,3,4].map(i => <div key={i} className="skeleton-row" />)}</div>
          ) : filteredMatches.length === 0 ? (
            <p className="muted" style={{ padding: '20px' }}>Aucun match</p>
          ) : (
            filteredMatches.map(m => {
              const isSelected = selectedMatch?.id === m.id;
              const counts = diffCounts[m.id];
              return (
                <div
                  key={m.id}
                  onClick={() => selectMatch(m)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                    background: isSelected ? 'var(--accent-dim)' : 'transparent',
                    borderLeft: isSelected ? '3px solid var(--accent)' : '3px solid transparent',
                    transition: 'all 0.1s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>{m.home_team} vs {m.away_team}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {format(new Date(m.match_date + 'T00:00:00'), 'EEE d MMM', { locale: fr })} · {m.match_time} · {m.competition}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {counts && (
                        <span style={{ fontSize: '11px', color: counts.confirmed > 0 ? 'var(--green)' : 'var(--text-dim)' }}>
                          {counts.confirmed}/{counts.total}
                        </span>
                      )}
                      <ChevronRight size={14} style={{ color: 'var(--text-dim)' }} />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT: Diffusions for selected match */}
      {selectedMatch && (
        <div>
          <div className="card" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ fontSize: '15px', fontWeight: 700 }}>
                  {selectedMatch.home_team} vs {selectedMatch.away_team}
                </h3>
                <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {format(new Date(selectedMatch.match_date + 'T00:00:00'), 'EEE d MMM', { locale: fr })} · {selectedMatch.match_time} · {selectedMatch.competition}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {selected.size > 0 && (
                  <>
                    <button className="btn btn-outline" onClick={() => bulkUpdateStatus('probable')} style={{ fontSize: '11px' }}>
                      Probable ({selected.size})
                    </button>
                    <button className="btn btn-green" onClick={() => bulkUpdateStatus('confirmed')} style={{ fontSize: '11px' }}>
                      <CheckCheck size={14} /> Confirmer ({selected.size})
                    </button>
                  </>
                )}
                <button className="btn btn-primary" onClick={() => setShowAddBar(!showAddBar)}>
                  <Plus size={14} /> Ajouter un bar
                </button>
              </div>
            </div>
          </div>

          {/* Add bar panel */}
          {showAddBar && (
            <div className="card" style={{ marginBottom: '12px', padding: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600 }}>Ajouter un bar</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select value={addStatus} onChange={e => setAddStatus(e.target.value)} className="filter-select" style={{ fontSize: '12px' }}>
                    <option value="unconfirmed">Non vérifié</option>
                    <option value="probable">Probable</option>
                    <option value="confirmed">Confirmé</option>
                  </select>
                  <button className="btn-icon" onClick={() => { setShowAddBar(false); setAddBarSearch(''); }}><X size={16} /></button>
                </div>
              </div>
              <div className="filter-search" style={{ marginBottom: '8px' }}>
                <Search size={14} />
                <input
                  placeholder="Rechercher un bar..."
                  value={addBarSearch}
                  onChange={e => setAddBarSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                {availableBars
                  .filter(b => !addBarSearch || b.name.toLowerCase().includes(addBarSearch.toLowerCase()) || (b.address || '').toLowerCase().includes(addBarSearch.toLowerCase()))
                  .map(b => (
                    <div
                      key={b.id}
                      onClick={() => { setAddBarId(b.id); }}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 12px', cursor: 'pointer', borderRadius: '6px',
                        background: addBarId === b.id ? 'var(--accent-dim)' : 'transparent',
                        border: addBarId === b.id ? '1px solid var(--accent)' : '1px solid transparent',
                        marginBottom: '2px', transition: 'all 0.1s',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>{b.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{b.address}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {b.probability != null && (
                          <span style={{ fontSize: '11px', fontWeight: 600, color: (b.probability ?? 0) >= 80 ? 'var(--green)' : (b.probability ?? 0) >= 60 ? 'var(--orange)' : 'var(--text-dim)' }}>
                            {b.probability}%
                          </span>
                        )}
                        {addBarId === b.id && (
                          <button className="btn btn-primary" onClick={(e) => { e.stopPropagation(); handleAddBarToMatch(); }} style={{ fontSize: '11px', padding: '5px 10px' }}>
                            Ajouter
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                }
                {availableBars.filter(b => !addBarSearch || b.name.toLowerCase().includes(addBarSearch.toLowerCase())).length === 0 && (
                  <p className="muted" style={{ padding: '12px' }}>Aucun bar disponible</p>
                )}
              </div>
            </div>
          )}

          {/* Diffusions table */}
          <div className="card" style={{ padding: diffusions.length > 0 ? 0 : '20px' }}>
            {loadingDiffs ? (
              <div className="skeleton-list">{[1,2,3].map(i => <div key={i} className="skeleton-row" />)}</div>
            ) : diffusions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px' }}>
                <p className="muted" style={{ marginBottom: '12px' }}>Aucun bar ne diffuse ce match</p>
                <button className="btn btn-primary" onClick={() => setShowAddBar(true)}>
                  <Plus size={14} /> Ajouter un bar
                </button>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '32px' }}>
                      <input type="checkbox"
                        checked={selected.size === diffusions.length && diffusions.length > 0}
                        onChange={() => {
                          if (selected.size === diffusions.length) setSelected(new Set());
                          else setSelected(new Set(diffusions.map(d => d.id)));
                        }}
                      />
                    </th>
                    <th>Bar</th>
                    <th>Prob.</th>
                    <th>Statut</th>
                    <th>Confirmé par</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {diffusions.map(d => {
                    const barName = d.bars?.name || '?';
                    const prob = (d.bars as any)?.probability;
                    return (
                      <tr key={d.id}>
                        <td><input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)} /></td>
                        <td style={{ fontWeight: 600 }}>{barName}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <button
                              className="btn-icon"
                              style={{ padding: '2px', opacity: 0.5 }}
                              onClick={() => handleProbChange(d.bar_id, barName, prob ?? 0, Math.max(0, (prob ?? 0) - 10))}
                            ><Minus size={12} /></button>
                            <input
                              type="number"
                              min="0" max="100" step="10"
                              value={prob ?? 0}
                              onChange={e => {
                                const v = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                handleProbChange(d.bar_id, barName, prob ?? 0, v);
                              }}
                              style={{
                                width: '42px', padding: '3px 2px', textAlign: 'center',
                                background: 'transparent', border: '1px solid var(--border)',
                                borderRadius: '4px', fontSize: '12px', fontWeight: 700,
                                color: (prob ?? 0) >= 80 ? 'var(--green)' : (prob ?? 0) >= 60 ? 'var(--orange)' : 'var(--red)',
                                outline: 'none',
                              }}
                            />
                            <button
                              className="btn-icon"
                              style={{ padding: '2px', opacity: 0.5 }}
                              onClick={() => handleProbChange(d.bar_id, barName, prob ?? 0, Math.min(100, (prob ?? 0) + 10))}
                            ><Plus size={12} /></button>
                          </div>
                        </td>
                        <td>
                          <select
                            className={`status-select ${d.status}`}
                            value={d.status}
                            onChange={e => handleStatusChange(d.id, e.target.value, d.status, barName)}
                          >
                            <option value="unconfirmed">Non vérifié</option>
                            <option value="probable">Probable</option>
                            <option value="confirmed">Confirmé</option>
                          </select>
                        </td>
                        <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {d.confirmed_by || '-'}
                          {d.confirmed_at && (
                            <span style={{ marginLeft: '6px', color: 'var(--text-dim)' }}>
                              {format(new Date(d.confirmed_at), 'dd/MM HH\'h\'mm', { locale: fr })}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn-icon danger" onClick={() => handleRemoveDiffusion(d)} title="Retirer">
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Summary */}
          {diffusions.length > 0 && (
            <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
              <span>{diffusions.length} bars</span>
              <span style={{ color: 'var(--green)' }}>{diffusions.filter(d => d.status === 'confirmed').length} confirmés</span>
              <span style={{ color: 'var(--orange)' }}>{diffusions.filter(d => d.status === 'probable').length} probables</span>
              <span style={{ color: 'var(--red)' }}>{diffusions.filter(d => d.status === 'unconfirmed').length} non vérifiés</span>
            </div>
          )}
        </div>
      )}

      {/* Placeholder when no match selected */}
      {!selectedMatch && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)' }}>
          <p>Sélectionne un match pour gérer ses diffusions</p>
        </div>
      )}
    </div>
  );
}
